import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Select } from '@bwoty-web/ui-kit';

const PaxSelect = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin-bottom: var(--space-400);
  background: var(--color-sunlight-200);
  padding: var(--space-300);

  /* Mobile only */
  @media (max-width: 767px) {
    max-width: 352px;
  }
`;

const PaxSelectRoom = styled.div`
  display: inline-flex;
  width: 100%;
`;

const ChildAges = styled.div`
  display: inline-flex;
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
`;

const StyledSelect = styled(Select)`
  background: var(--color-white);
  width: 50%;
  min-width: 120px;
  &:nth-of-type(odd):not(:last-of-type) {
    border-right: 1px solid var(--color-grey-100);
  }
  &:last-of-type:nth-of-type(odd) {
    width: calc(50% - 1px);
  }
  ${ChildAges} & {
    border-top: 1px solid var(--color-grey-100);
  }
  &:hover {
    background: var(--color-grey-50);
    transition: background 0.25s ease;
  }
  div:last-child:first-of-type {
    ${(props) => {
      if (props.invalid) {
        return css`
          border: 1px solid var(--color-border-error);
          margin: -1px;
          height: calc(100% + 2px);
        `;
      }
      return css`
        border: 0;
      `;
    }}
    div:first-child {
      line-height: 1.4rem;
    }
    div:last-child {
      font: var(--font-headline-s);
      max-width: 100%;
      margin-top: var(--space-100);
      padding-right: var(--space-400);
    }
  }
`;

const PaxSelectors = ({ bookingTexts, handleTravelAges, travelAges, missingPaxAge }) => {
  const PAX = [0, 1, 2, 3, 4, 5, 6];
  const childAges = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];

  const [adults, updateAdults] = useState(travelAges.filter((age) => Number(age) === 42));
  const [children, updateChildren] = useState(travelAges.filter((age) => Number(age) < 42));

  useEffect(() => {
    const newTravelAges = adults.concat(children);
    handleTravelAges(newTravelAges);
  }, [adults, children, handleTravelAges]);

  const handleAdultsChange = (num) => {
    if (num > adults.length) {
      updateAdults(() => {
        const newAdults = [...adults];
        for (let i = newAdults.length; i < num; i += 1) {
          newAdults.push('42');
        }
        return newAdults;
      });
    }
    if (num < adults.length) {
      updateAdults((prevState) => prevState.filter((adult, index) => index < num));
    }
  };

  const handleChildrenChange = (num) => {
    if (num > children.length) {
      updateChildren(() => {
        const newChildren = [...children];
        for (let i = newChildren.length; i < num; i += 1) {
          newChildren.push('-1');
        }
        return newChildren;
      });
    }
    if (num < children.length) {
      updateChildren((prevState) => prevState.filter((child, index) => index < num));
    }
  };

  const handleChildAgeChange = (age, index) => {
    updateChildren(children.map((child, childIndex) => (index === childIndex ? age : child)));
  };

  return (
    <PaxSelect>
      <PaxSelectRoom>
        <StyledSelect
          label={bookingTexts.adults}
          onChange={(e) => handleAdultsChange(e.target.value)}
          defaultValue={adults.length}
          items={PAX.filter((item) => item >= 1 && item < PAX.length - children.length)}
        />
        <StyledSelect
          label={bookingTexts.children}
          onChange={(e) => handleChildrenChange(e.target.value)}
          defaultValue={children.length}
          items={PAX.filter((item) => item < PAX.length - adults.length)}
        />
      </PaxSelectRoom>
      <ChildAges>
        {children.map((child, index) => (
          <StyledSelect
            label={bookingTexts.childAge}
            key={`child-${index}`}
            onChange={(e) => handleChildAgeChange(e.target.value, index)}
            defaultValue={Number(child) > -1 ? child : bookingTexts.choose}
            items={childAges}
            invalid={missingPaxAge && child === '-1'}
          />
        ))}
      </ChildAges>
    </PaxSelect>
  );
};

PaxSelectors.propTypes = {
  bookingTexts: PropTypes.objectOf(PropTypes.string).isRequired,
  handleTravelAges: PropTypes.func.isRequired,
  travelAges: PropTypes.arrayOf(PropTypes.string).isRequired,
  missingPaxAge: PropTypes.bool,
};

PaxSelectors.defaultProps = {
  missingPaxAge: false,
};

export default PaxSelectors;
